/* ==========================================================================
   PAMPER & PIECE — product-theme.js
   Reads a product's scent-universe metafields (exposed as data-* attributes)
   and writes them to CSS custom properties on :root, so the whole PDP — plus
   the cart drawer accent — repaints to match the scent. Also powers the hero
   scent selector's background-wash transitions.
   ========================================================================== */
(function () {
  'use strict';
  const Pamper = (window.Pamper = window.Pamper || {});
  const root = document.documentElement;

  // Map of data attribute -> CSS custom property.
  const MAP = {
    ppPrimary: '--pp-primary',
    ppSecondary: '--pp-secondary',
    ppBg: '--pp-bg',
    ppText: '--pp-text',
  };

  function readTheme(el) {
    const theme = {};
    Object.keys(MAP).forEach((k) => {
      if (el.dataset[k]) theme[MAP[k]] = el.dataset[k];
    });
    return theme;
  }

  // Relative luminance (WCAG) of a #rgb / #rrggbb colour, 0 (black) → 1 (white).
  function luminance(hex) {
    let h = String(hex).trim().replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6) return 1;
    const v = parseInt(h, 16);
    const f = (c) => {
      c /= 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * f((v >> 16) & 255) + 0.7152 * f((v >> 8) & 255) + 0.0722 * f(v & 255);
  }

  function contrast(a, b) {
    const la = luminance(a), lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  }

  // The scent colours are the exact box hexes — many are light pastels. Pick the
  // legible ink for text/icons that sit ON the primary fill (buttons, active
  // pills): whichever of white / the scent ink actually reads better on it.
  // Never alters the primary colour itself, only what's printed on top of it.
  Pamper.onColor = function (bg, darkInk) {
    const ink = darkInk || '#211d1c';
    return contrast('#ffffff', bg) >= contrast(ink, bg) ? '#ffffff' : ink;
  };
  Pamper.luminance = luminance;

  /**
   * Apply a theme object ({'--pp-primary': '#...'}) to a target element.
   * Defaults to :root so the universe washes across the page.
   */
  Pamper.applyTheme = function (theme, target) {
    const node = target || root;
    Object.keys(theme).forEach((prop) => node.style.setProperty(prop, theme[prop]));
    // Derive a few helpers from primary so glows/tints stay coherent.
    if (theme['--pp-primary']) {
      node.style.setProperty(
        '--pp-primary-glow',
        `color-mix(in srgb, ${theme['--pp-primary']} 40%, transparent)`
      );
      node.style.setProperty(
        '--pp-primary-tint',
        `color-mix(in srgb, ${theme['--pp-primary']} 12%, var(--pp-bg))`
      );
      // Legible ink on top of the (often pastel) primary fill.
      node.style.setProperty('--pp-on-primary', Pamper.onColor(theme['--pp-primary'], theme['--pp-text']));
    }
  };

  Pamper.readTheme = readTheme;

  /**
   * Derive a distinct, harmonious universe from a numeric seed (a product id).
   * MUST mirror snippets/scent-palette.liquid so server- and client-rendered
   * surfaces land on the same colours.
   */
  Pamper.derivePalette = function (seed) {
    const id = Number(seed) || 7;
    const hue = ((id * 137) % 360 + 360) % 360;
    const hue2 = (hue + 150) % 360;
    const light = hue >= 40 && hue <= 195 ? 40 : 50;
    const light2 = hue2 >= 40 && hue2 <= 195 ? 38 : 46;
    return {
      '--pp-primary': `hsl(${hue},70%,${light}%)`,
      '--pp-secondary': `hsl(${hue2},62%,${light2}%)`,
      '--pp-bg': `hsl(${hue},46%,97%)`,
      '--pp-text': `hsl(${hue},26%,13%)`,
    };
  };

  // ----- PDP / any element that opts into a scoped universe -----
  // [data-pp-theme="root"] washes the whole page; [data-pp-theme="scope"]
  // applies only to itself.
  document.querySelectorAll('[data-pp-theme]').forEach((el) => {
    let theme = readTheme(el);
    // Last-resort fallback: derive from a seed if no colours were rendered.
    if (!theme['--pp-primary'] && el.dataset.ppSeed) theme = Pamper.derivePalette(el.dataset.ppSeed);
    const scope = el.dataset.ppTheme === 'scope' ? el : root;
    Pamper.applyTheme(theme, scope);
    // Remember the active universe so the cart drawer can echo it.
    if (el.dataset.ppTheme === 'root') {
      Pamper.activeTheme = theme;
    }
  });

  // ----- Hero scent selector: wash background on hover/focus/select -----
  function initScentSelector(hero) {
    if (!hero || hero.dataset.scentBound === 'true') return;
    hero.dataset.scentBound = 'true';
    const stage = hero.querySelector('[data-scent-stage]') || hero;
    const triggers = hero.querySelectorAll('[data-scent-trigger]');
    // The groovy-wave site header follows the previewed scent too — hovering a
    // hero scent DELIBERATELY recolours the header's wave (its ribbons read these
    // --pp-* vars), so it changes on purpose rather than via glass see-through.
    const header = document.querySelector('[data-header]');
    let activeKey = null;

    const wash = (trigger) => {
      if (!trigger) return;
      const theme = readTheme(trigger);
      Pamper.applyTheme(theme, stage);
      if (header) Pamper.applyTheme(theme, header);
      triggers.forEach((t) => t.setAttribute('aria-selected', t === trigger ? 'true' : 'false'));
      activeKey = trigger.dataset.scentKey;
      // Swap the showcased product image. The server-rendered <img> carries a
      // srcset built from the FIRST scent; since srcset wins over src, it MUST be
      // cleared or every other scent keeps showing the first product's image.
      // When a scent has no image of its own, show the empty placeholder rather
      // than leaving the previously selected scent's image in place.
      const img = hero.querySelector('[data-scent-feature-img]');
      if (img) {
        img.style.opacity = '0';
        setTimeout(() => {
          img.removeAttribute('srcset');
          if (trigger.dataset.image) {
            img.src = trigger.dataset.image;
            img.classList.remove('hero__img--empty');
          } else {
            img.removeAttribute('src');
            img.classList.add('hero__img--empty');
          }
          img.alt = trigger.dataset.title || '';
          img.style.opacity = '1';
        }, 180);
      }
      const title = hero.querySelector('[data-scent-feature-title]');
      if (title && trigger.dataset.title) title.textContent = trigger.dataset.title;
      const mood = hero.querySelector('[data-scent-feature-mood]');
      if (mood && trigger.dataset.mood) mood.textContent = trigger.dataset.mood;
      const link = hero.querySelector('[data-scent-feature-link]');
      if (link && trigger.dataset.url) link.href = trigger.dataset.url;
      // Reveal the shown scent's floating flavour cloud (accords + "best for")
      // and hide the others, matching on the scent key the chips carry.
      hero.querySelectorAll('[data-scent-cloud]').forEach((cloud) => {
        cloud.hidden = cloud.dataset.scentKey !== activeKey;
      });
    };

    // Only hover-capable (desktop / mouse) devices preview on hover+focus. On
    // touch, tapping a <button> fires focus (and a synthesized mouseenter)
    // BEFORE click — which would wash the scent and set activeKey, so the very
    // first tap would satisfy the "already active" check below and jump to the
    // PDP without ever previewing. Skipping the hover listeners on touch lets a
    // first tap preview and a second tap on the active chip navigate.
    const canHover = window.matchMedia('(hover: hover)').matches;
    triggers.forEach((trigger) => {
      if (canHover) {
        ['mouseenter', 'focus'].forEach((ev) => trigger.addEventListener(ev, () => wash(trigger)));
      }
      trigger.addEventListener('click', (e) => {
        if (trigger.tagName !== 'BUTTON') return;
        // First interaction previews the scent; clicking the already-active
        // scent again navigates to its product page. On desktop the hover has
        // already selected it, so a single click goes straight to the PDP; on
        // touch the first tap previews and the second tap navigates.
        if (trigger.dataset.url && activeKey === trigger.dataset.scentKey) {
          window.location.href = trigger.dataset.url;
          return;
        }
        e.preventDefault();
        wash(trigger);
      });
    });

    // Expose the swap so theme-editor events can preview a chosen scent.
    hero.scentWash = wash;

    // Initialise with the first scent.
    if (triggers.length) wash(triggers[0]);
  }

  function bootScentSelectors(scope) {
    const root = scope || document;
    if (root.matches && root.matches('[data-scent-selector]')) initScentSelector(root);
    if (root.querySelectorAll) {
      root.querySelectorAll('[data-scent-selector]').forEach(initScentSelector);
    }
  }

  bootScentSelectors();

  // ----- Theme editor: rebind on section reload, preview the selected block -----
  // Editing a scent block (e.g. assigning its product) re-renders the section
  // HTML, which drops the listeners bound above and snaps the feature image back
  // to the first scent. Rebind on load, remember which block is being edited, and
  // re-preview it so a freshly-assigned second product shows ITS image — not the
  // first scent's.
  let lastSelectedBlockId = null;

  const previewBlock = (root, blockId) => {
    if (!root || !blockId || !root.querySelector) return;
    const trigger = root.querySelector('[data-scent-trigger][data-block-id="' + blockId + '"]');
    const hero = trigger && trigger.closest('[data-scent-selector]');
    if (hero) {
      initScentSelector(hero);
      if (hero.scentWash) hero.scentWash(trigger);
    }
  };

  document.addEventListener('shopify:section:load', (e) => {
    bootScentSelectors(e.target);
    previewBlock(e.target, lastSelectedBlockId);
  });
  document.addEventListener('shopify:block:select', (e) => {
    const target = e.target;
    let trigger = null;
    if (target.matches && target.matches('[data-scent-trigger]')) {
      trigger = target;
    } else if (e.detail && e.detail.blockId && target.querySelector) {
      trigger = target.querySelector('[data-scent-trigger][data-block-id="' + e.detail.blockId + '"]');
    }
    if (!trigger && target.querySelector) trigger = target.querySelector('[data-scent-trigger]');
    const hero = trigger && trigger.closest('[data-scent-selector]');
    if (hero) {
      initScentSelector(hero);
      lastSelectedBlockId = trigger.dataset.blockId || null;
      if (hero.scentWash) hero.scentWash(trigger);
    }
  });
  document.addEventListener('shopify:block:deselect', () => {
    lastSelectedBlockId = null;
  });
})();
